import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faNewspaper,
  faBullhorn,
  faCreditCard,
  faExclamationTriangle,
  faShieldAlt,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket,
  faEye,
  faLock
} from '@fortawesome/free-solid-svg-icons';

const UserGuideline = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const guidelines = [
    {
      id: 1,
      title: "General Conduct",
      icon: faUsers,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      items: [
        "Respect all members of the community – abusive, offensive, or threatening behavior is strictly prohibited.",
        "Do not share or promote fraudulent, misleading, obscene, or illegal content.",
        "Use the platform responsibly and for its intended purpose only."
      ]
    },
    {
      id: 2,
      title: "Guidelines for Reporters",
      icon: faNewspaper,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      items: [
        "Submit only accurate and verified news/reports.",
        "Do not copy, plagiarize, or misuse content from other platforms without permission.",
        "Avoid spreading rumors, hate speech, or politically motivated false news.",
        "Ensure that your reports do not violate any copyright, privacy, or intellectual property laws."
      ]
    },
    {
      id: 3,
      title: "Guidelines for Advertisers",
      icon: faBullhorn,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      items: [
        "Only post advertisements that are genuine, legal, and appropriate.",
        "Misleading or fraudulent advertisements are strictly prohibited.",
        "Ads related to nudity, gambling, illegal gaming, drugs, or harmful products will not be accepted.",
        "Ensure that all ad content complies with Indian advertising laws and regulations."
      ]
    },
    {
      id: 4,
      title: "Guidelines for Readers",
      icon: faEye,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      borderColor: "border-orange-200",
      items: [
        "Readers should verify important information before acting on any report.",
        "Avoid spreading misinformation from unverified sources.",
        "Report any suspicious, harmful, or misleading content immediately to IINSAF."
      ]
    },
    {
      id: 5,
      title: "Payment & Wallet Usage",
      icon: faCreditCard,
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      borderColor: "border-indigo-200",
      items: [
        "All payments are processed through Razorpay Payment Gateway for safety.",
        "Refunds are not provided in cash; instead, eligible amounts are credited to the IINSAF Wallet.",
        "Wallet balance can only be used for platform services and is non-transferable and non-withdrawable."
      ]
    },
    {
      id: 6,
      title: "Prohibited Activities",
      icon: faExclamationTriangle,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      borderColor: "border-red-200",
      items: [
        "Fraud, scams, or misrepresentation",
        "Posting or promoting nudity, adult content, or obscene material",
        "Illegal online gaming or gambling",
        "Using fake identities or accounts",
        "Attempting to hack, disrupt, or misuse the platform"
      ],
      warning: "Violators will face immediate account suspension and may also face legal action."
    },
    {
      id: 7,
      title: "Security & Responsibility",
      icon: faShieldAlt,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "from-emerald-50 to-emerald-100",
      borderColor: "border-emerald-200",
      items: [
        "Users must keep their login details confidential and secure.",
        "IINSAF is not responsible for any loss or misuse caused by user negligence.",
        "Each user is fully responsible for the content they submit or promote."
      ]
    },
    {
      id: 8,
      title: "Reporting Violations",
      icon: faPhone,
      color: "from-cyan-500 to-cyan-600",
      bgColor: "from-cyan-50 to-cyan-100",
      borderColor: "border-cyan-200",
      items: [
        "Phone: 9992396623",
        "Email: iinsafofficial@gmail.com",
        "Website: www.iinsaf.com"
      ]
    }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              USER GUIDELINES
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                Guidelines
              </span>{' '}
              – IINSAF
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At IINSAF, we aim to create a safe, transparent, and trustworthy platform for{' '}
              <span className="font-semibold text-blue-600">Reporters</span>,{' '}
              <span className="font-semibold text-green-600">Advertisers</span>, and{' '}
              <span className="font-semibold text-purple-600">Readers</span>. 
              To maintain fairness and integrity, all users must follow these guidelines while using our services.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Guidelines Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {guidelines.map((guideline, index) => (
            <motion.div
              key={guideline.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className={`p-6 sm:p-8 bg-gradient-to-r ${guideline.bgColor} border-b ${guideline.borderColor}`}>
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-r ${guideline.color} rounded-xl flex items-center justify-center mr-4 shadow-lg`}>
                    <FontAwesomeIcon icon={guideline.icon} className="text-white text-xl" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {guideline.id}. {guideline.title}
          </h2>
          </div>
                
                <div className="space-y-3">
                  {guideline.items.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <FontAwesomeIcon 
                        icon={faCheckCircle} 
                        className={`text-${guideline.color.split('-')[1]}-500 mt-1 mr-3 flex-shrink-0`} 
                      />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                  
                  {guideline.warning && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: guideline.items.length * 0.1 }}
                      className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-3" />
                        <span className="text-red-700 font-semibold">{guideline.warning}</span>
                      </div>
                    </motion.div>
                  )}
                </div>
            </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Need Help?</h2>
          </div>
          <p className="text-white/90 mb-6 text-lg">
            Have questions about our guidelines or need to report a violation? Our support team is here to help.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:9992396623" className="text-white/90 hover:text-white transition-colors duration-300">
                  9992396623
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                iinsafofficial@gmail.com
              </a>
            </div>
          </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                  www.iinsaf.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UserGuideline;
